import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowUpRight, GitCompare, LayoutGrid, DoorOpen, Maximize2, BedDouble, Layers, ShieldCheck, Zap, Thermometer, Shirt, Mountain, Expand, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { HERO_IMAGES } from "@/data/products";
import { Marquee } from "@/components/Marquee";
import { DemoBanner } from "@/components/DemoBanner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { trackMetaEvent } from "@/components/Analytics";
import { BushCabinProofLightbox } from "@/components/BushCabinProofLightbox";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";

const BUSHCABIN_MARQUEE = [
  "FRAME TESTED TO 2.5 TONNES",
  "TIG-WELDED ALUMINIUM",
  "MADE IN SOUTH AFRICA",
  "OEM-GRADE FINISH",
  "EXPEDITION TESTED",
];

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

const INTERIOR_GALLERY = [
  { src: "/products/bushcabin/bushcabin-interior-exterior-view.jpg", alt: "Bush Cabin exterior view with the rear door open" },
  { src: "/products/bushcabin/bushcabin-interior-wide-view.jpg", alt: "Bush Cabin interior wide view looking toward the open rear door", caption: "Sample fitout only — other options available" },
  { src: "/products/bushcabin/bushcabin-interior-tablebench.jpg", alt: "Bush Cabin interior showing the fold-out table and bench seating", caption: "Sample fitout only — other options available" },
  { src: "/products/bushcabin/bushcabin-interior-door-dusk.jpg", alt: "Bush Cabin interior looking out through the open rear door at dusk", caption: "Sample fitout only — other options available" },
  { src: "/products/bushcabin/bushcabin-interior-dinner-table.webp", alt: "Dinner on the Bush Cabin drop-down table with a magazine, looking out over the Ashburton riverbed" },
  { src: "/products/bushcabin/bushcabin-interior-mesh-windows.jpg", alt: "Bush Cabin tent mesh windows" },
  { src: "/products/bushcabin/bushcabin-interior-mattress-thickness.webp", alt: "Close-up of the Bush Cabin tent mattress showing its thickness and quilted cover" },
  { src: "/products/bushcabin/bushcabin-interior-bed-drawer.jpg", alt: "Bush Cabin bed and drawer storage" },
  { src: "/products/bushcabin/bushcabin-interior-benchtop-dusk.webp", alt: "Bush Cabin interior benchtop looking out at dusk" },
  { src: "/products/bushcabin/bushcabin-interior-power-panel-dusk.webp", alt: "Bush Cabin interior power panel and gas strut at dusk" },
  { src: "/products/bushcabin/bushcabin-interior-reardoor-open-dusk.webp", alt: "Bush Cabin rear door open showing interior shelving at dusk" },
  { src: "/products/bushcabin/bushcabin-interior-bed-shelving-night.webp", alt: "Bush Cabin interior bed and shelving at night" },
  { src: "/products/bushcabin/bushcabin-interior-tent-ocean-view.webp", alt: "Bush Cabin tent mattress with ocean view through the mesh door" },
  { src: "/products/bushcabin/bushcabin-interior-laptop-desk.webp", alt: "Working from the drop-down desk on the Bush Cabin bed" },
  { src: "/products/bushcabin/bushcabin-interior-girl-fridge.webp", alt: "Bush Cabin interior showing the Waeco fridge and bed storage drawers" },
].map(normalizeGalleryImage);

// Hero demo vehicle (Hilux) — our main-selling base cabin, shown popped up and
// closed. Restores the exterior photos people used to reach via the old
// per-vehicle detail pages, now that those links are no longer surfaced from
// this page.
const DESKTOP_HERO_IMAGE = "/products/bushcabin/bushcabin-hero-desktop-camp.webp";
const EXTERIOR_GALLERY = [
  { src: "/products/bushcabin/bushcabin-exterior-hilux-river-crossing.webp", alt: "Bush Cabin fitted to a Hilux fording a South Island river, mountains in the background", pos: "80% center" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-front-crossing-splash-aerial.webp", alt: "Bush Cabin fitted to a Hilux fording a river, front three-quarter aerial view with water splashing" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-dirtroad-valley.webp", alt: "Bush Cabin fitted to a Hilux driving away down a dirt track between South Island mountains" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-side-awning-cliff.webp", alt: "Bush Cabin fitted to a Hilux with the awning deployed, side profile view against a mountain cliff face" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-crossing-splash.webp", alt: "Bush Cabin fitted to a Hilux fording a river, rear three-quarter view with water splashing, mountains behind" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-front-crossing-creek.webp", alt: "Bush Cabin fitted to a Hilux fording a shallow South Island creek, grass banks either side" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-crossing-creek-splash.webp", alt: "Bush Cabin fitted to a Hilux fording a shallow rocky creek, rear view with water splashing" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-front-parked-riverbed.webp", alt: "Bush Cabin fitted to a Hilux with the roof tent popped up, front three-quarter view parked on a rocky riverbed" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-parked-river-mountains.webp", alt: "Bush Cabin fitted to a Hilux, rear view parked beside a South Island river, snow-capped mountains behind" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-closed-mountains.webp", alt: "Bush Cabin fitted to a Hilux, rear view with the tent closed, mountains in the background" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-front-open-beach.webp", alt: "Bush Cabin fitted to a Hilux with the roof tent popped up, front three-quarter view on the beach" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-side-awning-coast.webp", alt: "Bush Cabin fitted to a Hilux with the awning deployed, side view on the coast" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-awning-coast.webp", alt: "Bush Cabin fitted to a Hilux with the awning deployed, rear view on the coast" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-closed-beach.webp", alt: "Bush Cabin fitted to a Hilux, rear view with the tent closed, parked on a black sand beach" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-side-open-sunset.webp", alt: "Bush Cabin fitted to a Hilux with the roof tent popped up, side profile view at sunset" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-open-beach.webp", alt: "Bush Cabin fitted to a Hilux with the roof tent popped up, rear view on a black sand beach at sunset" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-side-dusk.webp", alt: "Bush Cabin fitted to a Hilux, side profile view on the beach at dusk" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-open-kitchen-sunset.webp", alt: "Bush Cabin fitted to a Hilux with the roof tent popped up and rear door open showing the kitchen module, on the beach at sunset" },
  { src: "/products/bushcabin/bushcabin-exterior-hilux-rear-kitchen-night-cooking.webp", alt: "Bush Cabin rear kitchen module open at night, cooking steak on the induction hob" },
].map(normalizeGalleryImage);

const PROOF_POINTS = [
  {
    icon: DoorOpen,
    img: "/products/bushcabin/proof/proof-rear-door.jpg",
    t: "A Proper Rear Door",
    d: "No tailgate to climb over. Bush Cabin's rear entry door and purpose-developed subframe are a properly engineered & dust sealed solution.",
  },
  {
    icon: Maximize2,
    img: "/products/bushcabin/proof/proof-side-openings.jpg",
    imgClassName: "object-[70%_center] md:object-center",
    t: "Openings This Big Aren't The Norm",
    d: "Openings this size aren't something many other camper frames can handle. Bush Cabin's comparatively huge gullwing openings are made possible by Bushtech's world proven proprietary frame designs — engineering that's load tested to 2.5 tonnes.",
  },
  {
    icon: BedDouble,
    img: "/products/bushcabin/proof/proof-sleep-live.jpg",
    t: "Sleep 2 Up Top. Live 2 Downstairs. At The Same Time.",
    d: "Sized much larger than a roof tent, Bush Cabin's sleeping compartment can sleep 2+ while 2 more sit comfortably below, with full camper height above them. No need for everyone to get up as soon as one person wants to. Cheaper campers, with only a standard sized roof tent, don't offer the same flexibility.",
  },
  {
    icon: Layers,
    img: "/products/bushcabin/proof/proof-fold-away.jpg",
    t: "The Bed Folds Away",
    d: "When you don't need to sleep, the bed folds away, opening up the full living space below.",
  },
  {
    icon: BedDouble,
    img: "/products/bushcabin/eezi-awn-mattress-100mm.jpg",
    t: "A Proper Mattress",
    d: "100mm of EEZI-AWN comfort, versus the 60-70mm typical of roof tents and cheaper ute campers.",
  },
  {
    icon: Thermometer,
    img: "/products/bushcabin/proof/proof-insulation.jpg",
    t: "Premium Thermal Insulation",
    d: "NZ spec Bush Cabins come with Bushtech's premium thermal insulation upgrade installed as standard. Insulating foam lines the aluminium panels in the cabin, while the sleeping compartment has an insulated roof panel and dual layered premium canvas.",
  },
  {
    icon: Shirt,
    img: "/products/bushcabin/proof/proof-canvas.jpg",
    t: "An Entirely Different Quality Of Canvas",
    d: "Core-spun poly-cotton canvas from South Africa offers quality, performance & longevity well beyond NZ roof-tent spec. It's also uniquely dual-layered through the sides.",
  },
  {
    icon: Zap,
    img: "/products/bushcabin/proof/proof-lighting-electrics.jpg",
    t: "Built-In Lighting & Electrics As Standard",
    d: "The same National Luna brand LED lighting system used in all our premium builds, built in and wired, along with device charging. Also pre-wired for solar on top.",
  },
  {
    icon: ShieldCheck,
    img: "/products/bushcabin/proof/proof-guarantee.jpg",
    t: "Load-Tested To 2.5 Tonnes",
    d: "2.5 tonne Land Cruiser on a Bushtech canopy. The same proprietary frame engineering behind Bush Cabin, proven on permanently off-road fleets in some of the harshest locations on Earth — in a different class.",
  },
];

export default function BushCabinPage() {
  const [models, setModels] = useState(null); // null = still loading
  const [accessories, setAccessories] = useState(null); // null = still loading
  const [comparisonHighlight, setComparisonHighlight] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showBuildCTA, setShowBuildCTA] = useState(false);
  const [youtubeAutoplay, setYoutubeAutoplay] = useState(false);
  const comparisonHighlightTimeout = useRef(null);

  // Callback ref (not useRef+useEffect) because this page renders a
  // "Loading…" placeholder until `models` arrives — the <video> element
  // doesn't exist in the DOM yet on first mount, so a [] dependency effect
  // would see a null ref once and never retry once the real hero mounts.
  // A callback ref fires exactly when the element is actually attached.
  const mobileHeroVideoRef = useCallback((video) => {
    if (!video) return;
    // Belt-and-braces for mobile autoplay reliability — some mobile browsers
    // evaluate autoplay eligibility strictly and are more consistent when
    // `muted` is also set imperatively (not just the HTML attribute) before
    // play() is attempted.
    video.muted = true;
    video.play?.().catch(() => {});
    // The source footage's loop point isn't seamless (the last frame doesn't
    // visually match the first), so instead of a hard jump-cut every loop,
    // fade the video out just before it restarts and back in just after —
    // dipping briefly to the page's own near-black background, which reads
    // as an intentional transition rather than a glitch. Uses rAF rather
    // than the `timeupdate` event, which fires too irregularly (as sparsely
    // as ~4x/sec in some browsers) to reliably catch a narrow 0.4s window.
    const FADE_SECONDS = 0.4;
    let rafId;
    const tick = () => {
      const { currentTime, duration } = video;
      if (duration) {
        const nearEnd = currentTime >= duration - FADE_SECONDS;
        const justLooped = currentTime < FADE_SECONDS;
        video.style.opacity = nearEnd || justLooped ? "0" : "1";
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Same callback-ref reasoning as mobileHeroVideoRef above — this section
  // is also below the `models === null` loading placeholder on first mount.
  // Starts the YouTube embed muted+autoplaying once the video itself (not
  // just the surrounding heading/copy) is ~40% visible, instead of forcing
  // it to play immediately on page load or waiting for a manual click.
  const youtubeObserverRef = useRef(null);
  const youtubeSectionRef = useCallback((node) => {
    if (!node) {
      youtubeObserverRef.current?.disconnect();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setYoutubeAutoplay(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    youtubeObserverRef.current = observer;
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/products/bushcabin`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
    axios.get(`${API}/api/products/bushcabinaccessory`).then((res) => setAccessories(res.data)).catch((err) => { console.error(err); setAccessories([]); });
    trackMetaEvent("ViewContent", {
      content_ids: ["bush-cabin"],
      content_name: "Bush Cabin",
      content_type: "product",
      currency: "NZD",
    });
    // Reveals the "Ready to build?" sticky CTA once the visitor has
    // scrolled roughly past the hero, so it doesn't compete with it.
    const onScroll = () => setShowBuildCTA(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(comparisonHighlightTimeout.current);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const scrollToComparison = (e) => {
    e.preventDefault();
    const el = document.getElementById("comparison");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    clearTimeout(comparisonHighlightTimeout.current);
    setComparisonHighlight(true);
    comparisonHighlightTimeout.current = setTimeout(() => setComparisonHighlight(false), 1800);
  };

  // Re-fires the same custom pixel event the old "Choose Your Platform"
  // model cards used to fire on click — that grid is gone (replaced by the
  // fork below), so every entry point that now carries the same "wants to
  // build a Bush Cabin" intent (the fork's own link, and both sticky
  // "start building" CTAs) re-fires it instead, keeping the Meta custom
  // conversion (built on this event name) alive.
  const trackBushCabinBuildClick = () => trackMetaEvent("BushCabinProductClick", {
    content_ids: ["bush-cabin"],
    content_name: "Bush Cabin",
    content_type: "product",
    currency: "NZD",
  }, undefined, true);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="bushcabin-page" className="pb-20 md:pb-0">
      <SEO
        path="/bush-cabin"
        title="Bushtech Bush Cabin Ute Camper NZ"
        description="Fully insulated live-in ute camper. Under 200kg. Load-tested to 2.5 tonnes. Internal sleeping access, EEZI-AWN 100mm mattress, optional diesel heater. From $24,990. See it in Christchurch."
        image={DESKTOP_HERO_IMAGE}
      />
      <BushCabinProofLightbox
        points={PROOF_POINTS}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />

      {/* Sticky mobile quick-nav */}
      <div
        data-testid="bushcabin-sticky-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--bg-main)]/95 backdrop-blur-md border-t border-[var(--border-default)] pl-4 pr-24 flex gap-2"
        style={{ paddingTop: "0.75rem", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <a
          href="#comparison"
          onClick={scrollToComparison}
          data-testid="bushcabin-sticky-compare-link"
          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[var(--border-default)] text-white text-xs uppercase tracking-wider py-3 hover:border-[var(--brand-primary)] transition-colors"
        >
          <GitCompare size={14} /> Compare
        </a>
        <a
          href="#models"
          data-testid="bushcabin-sticky-build-link"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[var(--brand-primary)] text-black text-xs uppercase tracking-wider py-3 font-semibold"
        >
          <Wrench size={14} /> Build
        </a>
      </div>

      {/* Sticky desktop "ready to build?" CTA — follows the scroll once
          past the hero, so the tool stays reachable without hunting for it.
          Anchored bottom-LEFT (not right) so it never sits on top of the
          Chatway chat bubble, and uses an opaque bg + gold accent border
          for clear legibility over busy photo content. Links to the fork
          (#models) rather than straight into the builder — same reasoning
          as the mobile "Build" button above. */}
      <div
        data-testid="bushcabin-sticky-build-cta-desktop"
        className={`hidden md:flex fixed bottom-6 left-6 z-40 items-center gap-4 bg-[var(--bg-main)] border border-[var(--border-default)] border-l-4 border-l-[var(--brand-primary)] shadow-2xl px-6 py-4 transition-all duration-300 ${showBuildCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <span className="font-display text-lg text-white">Ready to build your Bush Cabin?</span>
        <a href="#models" data-testid="bushcabin-sticky-build-cta-link" className="btn-primary whitespace-nowrap">
          Start Building <ArrowUpRight size={14} />
        </a>
      </div>

      {/* HERO — MOBILE (looping video, all text below in a solid block) */}
      <section className="md:hidden" data-testid="bushcabin-hero-mobile">
        <div className="relative aspect-[4/5] overflow-hidden bg-[var(--bg-main)]">
          <video
            ref={mobileHeroVideoRef}
            data-testid="bushcabin-hero-mobile-video"
            src="/products/bushcabin/bushcabin-hero-mobile.mp4"
            poster="/products/bushcabin/bushcabin-hero-mobile-poster.jpg"
            preload="auto"
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover transition-opacity duration-[400ms] ease-in-out"
            style={{ objectPosition: "center 40%" }}
          />
        </div>
        <div className="bg-[var(--bg-main)] px-4 pt-8 pb-10">
          <p className="overline mb-4">— Bush Cabin · Insulated Live-In Ute Camper</p>
          <h1 className="font-display text-[10vw] leading-[0.85] text-white max-w-[14ch]">
            LIVE-IN COMFORT.<br />
            <span className="text-[var(--brand-primary)]">UNCOMPROMISED</span><br />
            4WD CAPABILITY.
          </h1>
          <p className="text-base font-medium text-white/80 leading-relaxed mt-6">
            A fully insulated live-in ute camper built to a true OEM standard. Under 200kg installed weight. Internal sleeping access. EEZI-AWN 100mm mattress. From $24,990.
          </p>
        </div>
      </section>

      {/* HERO — DESKTOP (full-bleed image + text overlay) */}
      <section className="hidden md:flex relative min-h-[100svh] items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={DESKTOP_HERO_IMAGE}
            alt="Bush Cabin fitted to a Hilux with the roof tent popped up, parked at the kitchen module with snow-capped mountains in the background"
            className="w-full h-full object-cover"
            style={{objectPosition: '78% 32%'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 from-0% via-black/20 via-22% to-transparent to-40%" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/15 via-transparent to-transparent md:from-black/40" />
        </div>
        <div className="relative z-10 container-x pb-0 pt-32 w-full flex justify-end">
          <div className="max-w-md text-right">
            <p className="overline mb-6">— Bush Cabin · Insulated Live-In Ute Camper</p>
            <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] text-white hero-text-shadow">
              LIVE-IN COMFORT.<br />
              <span className="text-[var(--brand-primary)]">UNCOMPROMISED</span><br />
              4WD CAPABILITY.
            </h1>
            <p className="text-lg lg:text-xl font-normal text-white/80 leading-relaxed mt-4">
              A fully insulated live-in ute camper built to a true OEM standard. Under 200kg installed weight. Internal sleeping access. EEZI-AWN 100mm mattress. From $24,990.
            </p>
          </div>
        </div>
      </section>

      {/* AUSTRALIAN SHIPPING CALLOUT */}
      <div data-testid="bushcabin-au-shipping-banner" className="bg-[var(--brand-primary)] py-3 md:py-3.5">
        <p className="container-x text-center font-mono text-[11px] md:text-sm tracking-[0.15em] uppercase text-black font-semibold">
          Australian Customers —{" "}
          <a href="#enquire" data-testid="bushcabin-au-shipping-link" className="underline hover:no-underline">
            Contact us for a custom freight quote
          </a>
        </p>
      </div>

      <Marquee items={BUSHCABIN_MARQUEE} />

      {/* INTRO */}
      <section className="container-x pt-14 md:pt-20 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— The Bush Cabin</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              A FULLY INSULATED,<br />
              SECURE LIVING SPACE<br />
              <span className="text-[var(--brand-primary)]">ON A UTE.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
            <p>
              The Bushtech Bush Cabin combines the comfort of a compact camper with the capability of a properly prepared 4WD — built on the same South African engineering that's proven Bushtech canopies worldwide.
            </p>
            <p>
              At <span className="text-white">under 200 kg installed weight</span>, the Bush Cabin is exceptionally light for a fully integrated ute camper — yet load-tested to 2.5 tonnes.
            </p>
            <p>
              The result is a ute camper that retains the durability, balance and driving characteristics needed for long-distance touring, remote 4WD tracks and everyday use.
            </p>
          </div>
        </div>
      </section>

      {/* VIDEO — YouTube Short. Moved up here (right after Intro, before
          Story Split) since this is now a fuller 60-75s overview that works
          better as an early hook before the detailed photo/feature sections
          below — most mobile visitors won't scroll far enough to reach a
          video buried after two full galleries. Heading now sits above
          (full width) instead of beside the video so the video itself can
          be sized larger. */}
      <section className="container-x pt-4 pb-20 md:pt-8 md:pb-24 text-center">
        <p className="overline mb-4">— See It In Action</p>
        <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
          BUSH CABIN<br />
          <span className="text-[var(--brand-primary)]">IN 60 SECONDS</span>
        </h2>
        <p className="text-[var(--text-secondary)] max-w-xl mx-auto mt-5 leading-relaxed">
          A short walkthrough showing the Bush Cabin in real-world use — the pop-roof, gullwing side doors, tailgate table and interior layout in a matter of seconds.
        </p>

        <div ref={youtubeSectionRef} className="mx-auto max-w-sm md:max-w-md lg:w-[500px] relative aspect-[9/16] w-full border border-[var(--border-default)] bg-black overflow-hidden mt-10">
          <iframe
            data-testid="bushcabin-youtube"
            src={`https://www.youtube.com/embed/Sc5p__UVoyc?playsinline=1&rel=0&modestbranding=1&loop=1&playlist=Sc5p__UVoyc${youtubeAutoplay ? "&autoplay=1&mute=1" : ""}`}
            title="Bush Cabin walkthrough"
            loading="lazy"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>

        <a href="#enquire" data-testid="video-cta" className="btn-secondary mt-10 inline-flex">
          Book A Viewing
        </a>
      </section>

      {/* STORY SPLIT — exterior hero backdrop + interior inset */}
      <section data-testid="bushcabin-story-split" className="relative">
        <div className="relative min-h-[62vh] md:min-h-[92vh] overflow-hidden">
          <img
            src={HERO_IMAGES.home}
            alt="Bush Cabin fitted Hilux driving away down a dirt track between symmetric South Island mountains"
            className="w-full h-full object-cover absolute inset-0"
            style={{ objectPosition: "center 45%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/45 via-transparent via-30% to-transparent" />

          <div className="absolute inset-0 z-10 flex items-start">
            <div className="px-6 md:px-16 pt-24 md:pt-32">
              <p className="overline mb-4">— Set Up Wherever You Stop</p>
              <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] hero-text-shadow max-w-sm md:max-w-md">
                OUTSIDE, IT'S WILD.<br />
                <span className="text-[var(--brand-primary)]">INSIDE, IT'S HOME.</span>
              </h2>
            </div>
          </div>

          {/* Framed interior inset */}
          <div
            data-testid="bushcabin-story-split-inset"
            className="hidden md:block absolute right-8 lg:right-16 bottom-10 lg:bottom-16 w-[260px] lg:w-[340px] aspect-[4/3] border-2 border-[var(--bg-main)] shadow-2xl overflow-hidden group"
          >
            <img
              src={HERO_IMAGES.bushcabin2}
              alt="Dinner on the Bush Cabin drop-down table, looking out over the Ashburton riverbed"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90 leading-relaxed">
              Fully insulated interior — with a view
            </p>
          </div>

          {/* Weather/insulation callout — overlaid bottom-left on desktop,
              clear of the truck (center) and the interior inset
              (bottom-right). Hidden on mobile: at mobile's shorter aspect
              ratio this box was tall enough to fully cover the truck photo,
              so on mobile it instead renders as a plain block below (see
              bushcabin-weather-callout-mobile, after this section). */}
          <div
            data-testid="bushcabin-weather-callout"
            className="hidden md:block absolute md:left-16 bottom-8 md:bottom-16 max-w-xs lg:max-w-sm z-10 bg-black/55 backdrop-blur-sm p-4 md:p-5"
          >
            <h3 className="font-display text-lg md:text-2xl text-white leading-[1.1]">
              AN INSULATED & HEATABLE LIVE-IN SPACE IS GAME CHANGING WHEN THE WEATHER TURNS.
            </h3>
            <p className="text-xs md:text-sm text-white/85 leading-relaxed mt-3">
              <span className="text-white font-semibold">No external ladder.</span> Access the sleeping area internally — in the dark, in the wet, without stepping outside.
            </p>
            <p className="text-xs md:text-sm text-white/85 leading-relaxed mt-2">
              <span className="text-white font-semibold">A properly engineered rear door.</span> Full-height access with none of the dust-sealing issues a tailgate brings.
            </p>
          </div>
        </div>

        {/* Mobile: interior shot stacks below since the inset is hidden */}
        <div className="md:hidden relative aspect-[4/3] overflow-hidden">
          <img
            src={HERO_IMAGES.bushcabin2}
            alt="Dinner on the Bush Cabin drop-down table, looking out over the Ashburton riverbed"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <p className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90">
            Fully insulated interior — with a view
          </p>
        </div>
      </section>

      {/* Mobile-only: the weather/insulation callout as a plain block (not
          an overlay) sitting between the story-split section above and the
          full-bleed "opened up" image below — on mobile the overlaid
          version completely covered the truck photo, so this is the
          non-overlay equivalent for mobile only; desktop keeps the overlay
          version inside the story-split section above. */}
      <div data-testid="bushcabin-weather-callout-mobile" className="md:hidden px-6 py-8 bg-[var(--bg-main)]">
        <h3 className="font-display text-xl text-white leading-[1.1]">
          AN INSULATED & HEATABLE LIVE-IN SPACE IS GAME CHANGING WHEN THE WEATHER TURNS.
        </h3>
        <p className="text-sm text-white/85 leading-relaxed mt-3">
          <span className="text-white font-semibold">No external ladder.</span> Access the sleeping area internally — in the dark, in the wet, without stepping outside.
        </p>
        <p className="text-sm text-white/85 leading-relaxed mt-2">
          <span className="text-white font-semibold">A properly engineered rear door.</span> Full-height access with none of the dust-sealing issues a tailgate brings.
        </p>
      </div>

      {/* FULL BLEED — scroll-reveal. Heading/copy sit below the image (not
          overlaid) so the vehicle is fully visible, not covered by text. */}
      <ScrollReveal
        src={HERO_IMAGES.bushCabinOpened}
        alt="Bush Cabin opened up in warm weather"
        className="h-[55vh] md:h-[95vh] bg-black"
        objectPosition="center center"
        objectFit="cover"
      />

      <section className="bg-[var(--bg-main)] pt-10 md:pt-16">
        <div className="container-x">
          <p className="overline mb-4">— Two Iconic Brands, One Benchmark Camper</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-4xl">
            BUSHTECH ALUMINIUM<br />
            <span className="text-[var(--brand-primary)]">×</span> EEZI-AWN CANVAS.
          </h2>
          <p className="text-base md:text-lg mt-5 max-w-2xl leading-relaxed text-[var(--text-secondary)]">
            Engineered by two of South Africa's leading, globally proven overland brands with decades of experience — a camper system combining exceptional strength with premium sleeping comfort.
          </p>
        </div>
      </section>

      {/* BRAND EXPLANATIONS — black space between the Bushtech/EEZI-AWN
          full-bleed image above and the mattress callout below */}
      <section className="bg-[var(--bg-main)] py-16 md:py-20">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <p className="overline mb-3">— Bushtech</p>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-snug">
              Experts In Aluminium Engineering, Proven Worldwide.
            </h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              Years spent engineering aluminium canopies for heavily laden, permanently off-road fleets in some of the harshest environments on Earth have made Bushtech a <span className="text-white font-semibold">world leader in lightweight strength and advanced dust sealing.</span>
            </p>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              That same engineering, proven through years of corrugations, dust and hard use across Africa, is built into the Bush Cabin.
            </p>
          </div>
          <div>
            <p className="overline mb-3">— EEZI-AWN</p>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-snug">
              40+ Years Of True Expedition-Grade Roof Tents.
            </h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              Decades of experience, proven across every major continent on Earth. EEZI-AWN builds world-leading expedition roof tents, using vastly superior canvas known to last decades, not seasons, and roof tent mattresses widely regarded as the best in the world.
            </p>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              This is a level of quality, durability and experience <span className="text-white font-semibold">leagues apart from roof tents the New Zealand market has typically had access to</span> — and it's built into the Bush Cabin.
            </p>
          </div>
        </div>
      </section>

      {/* SEE INSIDE — interior gallery */}
      <section className="bg-[var(--bg-main)] pb-16 md:pb-20">
        <div className="container-x">
          <p className="overline mb-3 text-center">— Step Inside</p>
          <h2 className="font-display text-4xl md:text-6xl text-white text-center mb-8 md:mb-10">
            See Inside
          </h2>
          <div className="max-w-3xl md:max-w-6xl mx-auto" data-testid="bushcabin-interior-gallery">
            <ProductGallery gallery={INTERIOR_GALLERY} productName="Bush Cabin interior" thumbGridClassName="grid-cols-6" />
          </div>
          <p
            data-testid="bushcabin-fitout-note"
            className="max-w-3xl mx-auto text-center text-sm text-[var(--text-secondary)] italic mt-6"
          >
            The interior fit-out shown is one option only and not included. We can supply the lightweight aluminium framing shown in the photos if required. We're currently working with a specialist designer to develop modular turnkey interior packages, through to self-contained certified if required.
          </p>

          <div className="max-w-4xl mx-auto border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden mt-10" data-testid="bushcabin-self-contained-section">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-10 flex flex-col justify-center order-2 md:order-1">
                <p className="overline mb-3">— Self-Contained Options</p>
                <h3 data-testid="bushcabin-self-contained-heading" className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">
                  Need A Fully Certified Self-Contained Bush Cabin Build?
                </h3>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                  Talk to us about the options. Some customers request a fully certified self-contained build — others don't need one.
                </p>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
                  For those who do, we're working with one of NZ's premier van interior fitout firms to deliver interior fitout options suited to your needs.
                </p>
                <a href="#enquire" data-testid="bushcabin-self-contained-cta" className="btn-secondary mt-8 self-start">
                  Talk To Us About The Options
                </a>
              </div>
              <div className="relative order-1 md:order-2 bg-[#F5F0E8] flex items-center justify-center p-6 md:p-10">
                <img
                  src="/products/bushcabin/proof/self-contained-layout.png"
                  alt="Example self-contained interior layout options for the Bush Cabin"
                  className="w-full h-auto max-w-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXTERIOR PHOTOS — hero demo vehicle (Hilux), our main selling model */}
      <section className="bg-[var(--bg-main)] pb-16 md:pb-20">
        <div className="container-x">
          <p className="overline mb-3 text-center">— On The Hilux</p>
          <h2 className="font-display text-4xl md:text-6xl text-white text-center mb-8 md:mb-10">
            Exterior Photos
          </h2>
          <div className="max-w-3xl md:max-w-6xl mx-auto" data-testid="bushcabin-exterior-gallery">
            <ProductGallery gallery={EXTERIOR_GALLERY} productName="Bush Cabin exterior" thumbGridClassName="grid-cols-5" initialThumbCount={8} />
          </div>
        </div>
      </section>

      {/* MATTRESS CALLOUT */}
      <section className="container-x pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6 md:p-12">
          <div className="aspect-[4/3] overflow-hidden order-1 md:order-2">
            <img
              src="/products/bushcabin/eezi-awn-mattress-100mm.jpg"
              alt="100mm EEZI-AWN mattress fitted to the Bush Cabin"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="order-2 md:order-1">
            <p className="overline mb-4">— Premium Sleeping Comfort</p>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-[0.95]">
              100MM OF<br /><span className="text-[var(--brand-primary)]">EEZI-AWN COMFORT.</span>
            </h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] mt-5 leading-relaxed">
              <span className="text-white font-semibold">A proper mattress, not a roof tent compromise.</span> The Bush Cabin's premium 100mm EEZI-AWN mattress is in a different league from the 60–70mm mattresses typical of roof tents and cheaper ute campers. On a multi-night trip, that's a difference you'll feel every night.
            </p>
          </div>
        </div>
      </section>

      {/* PROOF POINTS — the specifics proven out in the Bush Cabin ad campaign,
          answering "why does it cost more" head-on with facts, not defensiveness. */}
      <section id="comparison" data-testid="bushcabin-proof-points" className="container-x py-24 md:py-32 border-t border-[var(--border-default)] scroll-mt-24">
        <div className="mb-12 max-w-3xl">
          <p className="overline mb-4">— Proof, Not Promises</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
            WHAT YOU CAN SEE.<br /><span className="text-[var(--brand-primary)]">AND WHAT YOU CAN'T.</span>
          </h2>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)] ${comparisonHighlight ? "anchor-pulse" : ""}`}>
          {PROOF_POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`bushcabin-proof-point-${i}`}
                onClick={() => { if (window.innerWidth >= 768) setLightboxIndex(i); }}
                className="relative overflow-hidden aspect-[4/5] md:aspect-square group md:cursor-pointer"
              >
                <img src={p.img} alt={p.t} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${p.imgClassName || ""}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
                <div className="hidden md:flex absolute top-5 right-5 w-9 h-9 items-center justify-center rounded-full bg-black/40 border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <Expand size={16} className="text-white" strokeWidth={1.5} />
                </div>
                <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-10">
                  <Icon size={26} className="text-[var(--brand-primary)] mb-5" strokeWidth={1.3} />
                  <h3 className="font-display text-xl text-white tracking-wide leading-tight">{p.t}</h3>
                  <p className="text-white/80 mt-3 leading-relaxed text-sm md:text-base">{p.d}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="max-w-3xl mt-14">
          <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
            A premium, purpose-built camper like the Bush Cabin sits in a different class from the cheaper end of the market.
          </p>
          <p className="text-base md:text-lg text-[var(--text-secondary)] mt-4 leading-relaxed">
            Cheaper ute campers are typically built around the basic architecture of a ute canopy with a roof tent above. Without a dedicated rear entry door, access means climbing over the ute's tailgate — while retaining the dust-sealing challenges tailgates are known for. Bush Cabin replaces the tailgate entirely with a purpose-engineered, dust-sealed rear entry door and purpose-developed subframe, providing direct, easy access into the cabin.
          </p>
          <p className="text-base md:text-lg text-[var(--text-secondary)] mt-4 leading-relaxed">
            <span className="text-white font-semibold">Bush Cabin's tent section is also purpose-built and substantially larger than a standard roof tent.</span> For users up to around 6ft tall, that extra space allows one person to remain comfortably in bed while the other sits downstairs or moves in and out of the sleeping area <span className="text-[var(--brand-primary)] font-semibold">— without disturbing their partner.</span> The benefit becomes particularly obvious during a middle-of-the-night bathroom trip: one person can get up and leave while the other stays comfortably in bed.
          </p>
          <p className="text-base md:text-lg text-[var(--text-secondary)] mt-4 leading-relaxed">
            <span className="text-white font-semibold">Bush Cabin also features exceptionally large side access openings, transforming how easily the interior can be accessed and used from outside.</span> Achieving openings of this size while retaining exceptional structural strength requires sophisticated frame engineering — something made possible by Bushtech's proprietary aluminium frame technology.
          </p>
          <p className="text-base md:text-lg text-[var(--text-secondary)] mt-4 leading-relaxed">
            Every NZ-spec Bush Cabin also comes standard with Bushtech's premium thermal insulation package. The aluminium cabin is lined throughout with insulating foam, while the tent section combines an insulated roof panel with premium South African core-spun polycotton canvas, dual-layered through the sides. Together, they deliver genuine four-season performance at a level rarely seen in the ute camper market.
          </p>
        </div>

        <div className="max-w-4xl mx-auto border border-[var(--border-default)] bg-[var(--bg-secondary)] p-8 md:p-10 mt-14" data-testid="bushcabin-vehicle-acquisition-block">
          <h4 className="font-display text-xl md:text-2xl text-white leading-tight mb-3">
            Don't Have The Vehicle Yet?
          </h4>
          <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
            We can assist you — from advising on acquiring a used or new vehicle, through everything needed to start with a stock standard vehicle and end up with a fully equipped Bush Cabin camper build.
          </p>
          <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
            We recommend getting in touch with us sooner rather than later, as it can take time to properly plan out a build like this in time for your intended travels.
          </p>
        </div>

        <div className="mt-14 max-w-4xl mx-auto flex items-start gap-4 border border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/[0.06] px-6 py-5 md:px-8 md:py-7" data-testid="bushcabin-offroad-callout">
          <Mountain size={26} className="text-[var(--brand-primary)] shrink-0 mt-0.5" strokeWidth={1.4} />
          <div>
            <p className="text-base md:text-lg text-white font-semibold leading-snug">How capable, off-road?</p>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-1.5">
              Many slide-on campers and ute-based motorhomes claim 4WD capability. As globally experienced 4WDers, our benchmark goes well beyond a gravel road. Bush Cabin is engineered for serious 4WD journeys well beyond the point where many ute-based motorhomes would have to turn back.
            </p>
          </div>
        </div>

        <div className="mt-14 max-w-4xl mx-auto border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden grid grid-cols-1 md:grid-cols-2" data-testid="bushcabin-xseries-callout">
          <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
            <img src="/products/bushcabin/proof/xseries-callout.jpg" alt="Bushtech X-Series Tray Canopy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90 leading-relaxed">
              Our Bushtech X-Series Demonstrator
            </p>
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <p className="overline mb-3">— Engineering Pedigree</p>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">Built By Bushtech</h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
              Bushtech is best known internationally for its commercial-duty aluminium canopies, proven on 4WDs in some of the harshest environments in the world.
            </p>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              Its aluminium ute canopy carries a 300kg dynamic load — 50% greater than its nearest competitor — while weighing less than even plastic and fibreglass canopies.
            </p>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
              The Bush Cabin comes from the same engineering team behind our formidable X-Series builds. Serious 4WD engineering is what Bushtech does.
            </p>
          </div>
        </div>
      </section>

      <DemoBanner product="Bushtech Bush Cabin" image={HERO_IMAGES.bushCabinDoors} showGalleryLink={false} />

      {/* MODELS */}
      <section id="models" className="container-x pt-16 pb-24 md:pt-20">
        {/* Two-path fork: routes a brand-new buyer to the builder, and an
            existing owner straight down to the accessories grid below —
            replaces the old 4-up "Choose Your Platform" grid, which is now
            reachable through the builder itself (pick a vehicle there to
            see photos/detail on its own page). */}
        <div className="text-center mb-10 md:mb-12">
          <p className="overline mb-4">— Where To Start</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
            STARTING OUT OR ALREADY HAVE A BUSH CABIN?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 md:gap-6">
          <Link
            to="/build-your-bush-cabin"
            data-testid="bushcabin-fork-new-link"
            onClick={trackBushCabinBuildClick}
            className="group block border-2 border-[var(--brand-primary)] bg-[var(--brand-primary)]/[0.05] hover:bg-[var(--brand-primary)]/[0.09] p-8 md:p-12 transition-colors"
          >
            <Wrench size={32} className="text-[var(--brand-primary)] mb-5" />
            <p className="overline mb-2">— New To Bush Cabin?</p>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-tight mb-3">Build & Price Yours</h3>
            <p className="text-[var(--text-secondary)] text-base leading-relaxed max-w-md">
              Pick your vehicle, add the accessories you want, and see your price update live as you go.
            </p>
            <span className="btn-primary mt-6">
              Start Building <ArrowUpRight size={14} />
            </span>
          </Link>
          <a
            href="#accessories"
            data-testid="bushcabin-fork-existing-link"
            className="group block border border-[var(--border-default)] hover:border-[var(--brand-primary)] bg-[var(--bg-secondary)] p-8 md:p-10 transition-colors"
          >
            <LayoutGrid size={28} className="text-[var(--brand-primary)] mb-5" />
            <p className="overline mb-2">— Already Own One?</p>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-3">Shop Accessories</h3>
            <p className="text-[var(--text-secondary)] text-base leading-relaxed">
              MOLLE panels, drop-down tables, awnings and more — add them to your Bush Cabin any time.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-mono uppercase tracking-wider text-[var(--brand-primary)] mt-6 group-hover:underline">
              View Accessories <ArrowUpRight size={14} />
            </span>
          </a>
        </div>

        {accessories && accessories.length > 0 && (
          <div id="accessories" className="mt-16 md:mt-20 pt-12 md:pt-16 border-t border-[var(--border-default)] scroll-mt-24" data-testid="bushcabin-accessories-section">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <p className="overline mb-4">— Options & Extras</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                  CHOOSE YOUR ACCESSORIES.
                </h2>
              </div>
              <p className="text-[var(--text-secondary)] max-w-md font-mono text-sm">
                Accessories ordered with a new pre-order build come factory installed at no extra
                charge. This doesn't apply to Bush Cabins already in NZ stock.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {accessories.map((a, i) => (
                <Link
                  key={a.slug}
                  to={`/bush-cabin-accessories/${a.slug}`}
                  data-testid={`bushcabin-accessory-card-${i}`}
                  className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <HoverImage primary={a.thumb} secondary={a.gallery?.[1]?.src} alt={a.name} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-display text-base md:text-lg text-white leading-tight">{a.name}</h3>
                      <p className="font-display text-[var(--brand-primary)] text-lg mt-2">
                        {a.priceOnRequest || a.price == null
                          ? (a.badge || "Enquire")
                          : `${a.priceFrom ? "FROM " : ""}$${a.price.toLocaleString("en-NZ")}`}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/build-your-bush-cabin" data-testid="bushcabin-build-your-cabin-link" className="btn-primary inline-flex">
                Build & Price Your Cabin <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
